import { Close as CloseIcon, FlashOff, FlashOn } from '@mui/icons-material';
import { Alert, Box, IconButton, Paper, Typography } from '@mui/material';
import jsQR from 'jsqr';
import { useEffect, useRef, useState } from 'react';

interface QRScannerProps {
  onScan: (result: string) => void;
  onClose?: () => void;
}

/**
 * Extracts tag_id from a scanned QR code URL or raw value.
 * Handles URLs like: https://app.dignicrm.com/asset/abc-123-def
 * Also handles raw tag_id values.
 */
function extractTagId(scannedValue: string): string {
  try {
    const url = new URL(scannedValue);
    // Extract tag_id from path like /asset/:tagId or /assets/:tagId
    const pathMatch = url.pathname.match(/\/assets?\/([a-zA-Z0-9-]+)$/);
    if (pathMatch) {
      return pathMatch[1];
    }
    // Return full value if no match
    return scannedValue;
  } catch {
    // Not a URL, return as-is (could be raw tag_id)
    return scannedValue;
  }
}

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [torch, setTorch] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  // const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let animationId: number;
    let active = true;

    async function startScanner() {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        if (!active) {
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }

        setStream(mediaStream);

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          await videoRef.current.play();
        }

        // setScanning(true);

        // Start scanning loop
        const scan = () => {
          if (!active || !videoRef.current || !canvasRef.current) return;

          const video = videoRef.current;
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');

          if (video.readyState === video.HAVE_ENOUGH_DATA && ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'dontInvert',
            });

            if (code && code.data) {
              const tagId = extractTagId(code.data);
              // setScanning(false);
              onScan(tagId);
              return; // Stop scanning after successful read
            }
          }

          animationId = requestAnimationFrame(scan);
        };

        scan();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to access camera. Please grant permission.'
        );
      }
    }

    startScanner();

    return () => {
      active = false;
      cancelAnimationFrame(animationId);
    };
  }, [onScan]);

  // Cleanup stream separately to avoid dependency issues
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const toggleTorch = async () => {
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };

    if (capabilities.torch) {
      await track.applyConstraints({
        advanced: [{ torch: !torch } as MediaTrackConstraintSet],
      });
      setTorch(!torch);
    }
  };

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please ensure camera permissions are granted and try again.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 400, mx: 'auto' }}>
      <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ position: 'relative' }}>
          <video ref={videoRef} style={{ width: '100%', display: 'block' }} playsInline muted />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Scanning overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              aspectRatio: '1',
              border: '3px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            }}
          />

          {/* Controls */}
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
            <IconButton
              onClick={toggleTorch}
              sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
              size="small"
            >
              {torch ? <FlashOn /> : <FlashOff />}
            </IconButton>
            {onClose && (
              <IconButton onClick={onClose} sx={{ bgcolor: 'rgba(255,255,255,0.9)' }} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography variant="body2" color="text.secondary">
            Position the QR code within the frame
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
